'use client';

import coinWhite from '@/assets/images/icons/coin-white.png';
import ActionButton from '@/components/form/ActionButton/ActionButton';
import IconTextUnit from '@/components/ui/IconTextUnit/IconTextUnit';
import ModalBase from '@/components/ui/ModalBase/ModalBase';
import { LOTTERY_STATUS, OAUTH_PROVIDER } from '@/constants';
import { Format } from '@/lib/dateFns';
import { swal } from '@/lib/sweetalert';
import { diffDateTimeHours, newDate } from '@/utils/date';
import { formatCurrency, formatDateCommon } from '@/utils/formatters';
import { goMainOneTimeAuth } from '@/utils/functions';
import { isAfter, isBefore } from 'date-fns';
import type { Session } from 'next-auth';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import type { LotteryEventDetail } from '../../types';
import LotteryDrawButton from '../LotteryDrawButton/LotteryDrawButton';
import styles from './LotteryFab.module.scss';

import utilsStyles from '@/styles/modules/utils.module.scss';
import type { Route } from 'next';

function LotteryFab({
  lotteryEvent,
  session = null,
  needsDAccountLink = false,
  daccountRegisterUrl,
}: {
  lotteryEvent: LotteryEventDetail;
  session?: Session | null;
  needsDAccountLink?: boolean;
  daccountRegisterUrl?: string | null;
}) {
  const [timeLeft, setTimeLeft] = useState('');
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [currentShopName, setCurrentShopName] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      if (shop) {
        setCurrentShopName(shop);
      }
    }
  }, []);

  useEffect(() => {
    const endAtDate = newDate(lotteryEvent.endAt);
    const interval = window.setInterval(() => {
      const currentDate = newDate();
      const diff = diffDateTimeHours(endAtDate, currentDate);
      setTimeLeft(`${diff.days}日${diff.hours}時間${diff.minutes}分${diff.seconds}秒`);
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [lotteryEvent.endAt]);

  const handleDaccountRegisterClick = () => {
    if (daccountRegisterUrl) {
      window.location.href = daccountRegisterUrl;
    } else {
      swal.messages({ messages: ['dアカウント登録URLの取得に失敗しました'] });
    }
  };

  const goLotteryConfirm = () => {
    if (needsDAccountLink) {
      //ログインしているかチェック
      if (!session?.user) {
        router.push(`/auth/signin?callbackUrl=/lotteries/${lotteryEvent.id}`);
        return;
      }
      setIsOpen(true);
      return;
    }
    // ★★★ shopパラメータを次のURLに引き継ぐ ★★★
    const baseUrl = `/lotteries/${lotteryEvent.id}/confirm`;
    if (currentShopName) {
      router.push(`${baseUrl}?shop=${currentShopName}` as Route);
    } else {
      router.push(baseUrl as Route);
    }
  };

  const generateDaccountRedirectUrl = (
    baseUrl: string,
    apiBaseUrl: string,
    serviceId: string,
    lotteryEventId: number,
  ): string => {
    const encodedCallbackUrl = encodeURIComponent(`${baseUrl}/lotteries/${lotteryEventId}`);
    const encodedCancelUrl = encodeURIComponent(`${baseUrl}/lotteries/${lotteryEventId}`);
    return `${apiBaseUrl}/oauth/v1/link/${OAUTH_PROVIDER.daccount.value}/redirect?oem_service_id=${serviceId}&callback_url=${encodedCallbackUrl}&cancel_url=${encodedCancelUrl}`;
  };

  const daccountRedirect = async () => {
    if (!session?.user) {
      router.push(`/auth/signin?callbackUrl=/lotteries/${lotteryEvent.id}`);
      return;
    }
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
    const serviceId = process.env.NEXT_PUBLIC_SERVICE_ID;
    const lotteryEventId = lotteryEvent.id;

    if (!baseUrl || !apiBaseUrl || !serviceId) {
      return;
    }

    const redirectUrl = generateDaccountRedirectUrl(baseUrl, apiBaseUrl, serviceId, lotteryEventId);
    window.location.href = redirectUrl;
  };

  return (
    <>
      <div className={styles.componentStyle}>
        <div className={styles.container}>
          <div>
            <div className={`${styles.itemCoin} ${utilsStyles.whiteColor}`}>
              <IconTextUnit text={formatCurrency(lotteryEvent.drawFee)} imgSrc={coinWhite.src} unit={'/回'} />
              <p className={utilsStyles.textXSmall}>{shippingFeeText(lotteryEvent)}</p>
              {isPeriodLottery(lotteryEvent.startAt, lotteryEvent.endAt) && (
                <p className={styles.timeLeft}>終了まで{timeLeft}</p>
              )}
            </div>
          </div>
          {rightArea(session, lotteryEvent, goLotteryConfirm, needsDAccountLink)}
        </div>
      </div>

      {/* OAuthモーダル */}
      <ModalBase
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        bgColor={'transparent'}
      >
        <div className={styles.modal}>
          <div className={styles.centerContent}>
            <p className={styles.modalTitle}>{lotteryEvent.name}の利用には</p>
            <p className={styles.modalTitle}>dアカウントの連携が必要です</p>

            <div className={`${utilsStyles.mtSmall} ${styles.buttonWrapper}`}>
              <button type='button' className={styles.daccountConnectButton} onClick={daccountRedirect}>
                dアカウントと連携する
              </button>
              <button
                type='button'
                className={`${styles.daccountRegisterButton}`}
                onClick={handleDaccountRegisterClick}
              >
                ※dアカウントをお持ちでない方はこちら
              </button>
            </div>
            <div className={utilsStyles.mtSmall}>
              <ActionButton onClick={() => setIsOpen(false)} outlined size='small'>
                キャンセル
              </ActionButton>
            </div>
          </div>
        </div>
      </ModalBase>
    </>
  );
}

function isPeriodLottery(startAt: string, endAt: string) {
  return endAt && nowLotteryStatus(startAt, endAt) === LOTTERY_STATUS.is_session.value;
}

function nowLotteryStatus(startAt: string, endAt: string) {
  if (endAt && isAfter(newDate(), newDate(endAt))) {
    return LOTTERY_STATUS.later.value;
  }
  if (isBefore(newDate(), newDate(startAt))) {
    return LOTTERY_STATUS.upcoming.value;
  }
  return LOTTERY_STATUS.is_session.value;
}

function requireAddress(session: Session | null) {
  return session?.user && !session.user.setAddress;
}

function rightArea(
  session: Session | null,
  lotteryEvent: LotteryEventDetail,
  goLotteryConfirm: () => void,
  needsDAccountLink: boolean,
) {
  const handleCustomPopup = async () => {
    const title = lotteryEvent.customOptions?.popUpMessageTitleLotteryEventEntry;
    const text = lotteryEvent.customOptions?.popUpMessageTextLotteryEventEntry;

    if (!title && !text) {
      goLotteryConfirm();
      return;
    }

    const { isConfirmed } = await swal.confirm({
      title,
      text,
      confirmButtonText: 'OK',
    });

    if (!isConfirmed) {
      return;
    }

    goLotteryConfirm();
  };

  switch (nowLotteryStatus(lotteryEvent.startAt, lotteryEvent.endAt)) {
    case LOTTERY_STATUS.upcoming.value:
      // 開催予定
      return (
        <div className={styles.upcomingButton}>
          <LotteryDrawButton disabled>
            <div className={utilsStyles.centeredVertical}>
              <p className={utilsStyles.textXSmall}>
                {formatDateCommon(lotteryEvent.startAt, Format.dateTimeLotteryJa)}
              </p>
              <p className={utilsStyles.textSmall}>スタート</p>
            </div>
          </LotteryDrawButton>
        </div>
      );
    case LOTTERY_STATUS.later.value:
      return <div className={styles.endTextWrapper}>このくじは終了しています</div>;
    default:
      // 住所入力必須
      return requireAddress(session) ? (
        <div className={styles.requireAddressButton}>
          <LotteryDrawButton color={'primary'} onClick={() => openAddressAlert(lotteryEvent.id)}>
            <div className={utilsStyles.centeredVertical}>
              <p className={utilsStyles.textSmall}>くじを引く前に</p>
              <p className={utilsStyles.textXSmall}>配送先情報を入力してください</p>
            </div>
          </LotteryDrawButton>
        </div>
      ) : (
        <div>
          <LotteryDrawButton color={'primary'} onClick={handleCustomPopup}>
            くじイベントに参加する
          </LotteryDrawButton>
          {needsDAccountLink && (
            <p className={`${utilsStyles.textXSmall} ${styles.daccountNote}`}>
              ※このイベントはdアカウント連携が必要です
            </p>
          )}
        </div>
      );
  }
}

async function openAddressAlert(lotteryEventId: number) {
  const { isConfirmed } = await swal.confirm({
    title: '配送先情報が未入力です',
    text: 'くじを引く前に配送先情報の入力が必要です',
    confirmButtonText: '配送先登録画面へ',
  });
  if (isConfirmed) {
    goMainOneTimeAuth({
      redirectPath: `/account/address?callback=${location.href}`,
      lotteryEventId: lotteryEventId,
      shouldTrack: true,
    });
  }
}

function shippingFeeText(event: LotteryEventDetail): string {
  if (event.shippingFee > 0 && event.additionalShippingFee > 0)
    return `（送料：初回と${event.additionalShippingFeePerTimes}回ごとに${event.shippingFee}coin）`;
  if (event.shippingFee > 0 && event.additionalShippingFee === 0) return `（送料：初回のみ${event.shippingFee}coin）`;
  if (event.shippingFee === 0 && event.additionalShippingFee === 0) return '（送料を含む）';

  return '';
}

export default LotteryFab;
